import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import type { Invoice, PlanId, PlanStatus, PlanTierOption, Settings } from "@vereinfach/types";
import { api, apiUrl, errorMessage, mutate } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useCurrentTenant } from "../../lib/currentTenant";
import { useSettings } from "../../lib/settings";
import { capLabel, formatCHF, formatDate, formatIsoDate, planLabel } from "../../lib/format";
import { Modal } from "../../components/Modal";
import { StatusBadge } from "../../components/StatusBadge";
import { Field, Section } from "./common";

const CONTACT_EMAIL = "hallo@vereinfach.app";

type PlanResponse = {
  plan: PlanStatus;
  tiers: PlanTierOption[];
  recommended_plan: PlanId;
  terms_version: string;
};

function tierRank(plan: PlanId): number {
  return ["frei", "klein", "mittel", "gross", "xl"].indexOf(plan);
}

export function SettingsAbo() {
  const tenant = useCurrentTenant();
  const { me, refetchMe } = useAuth();
  const [data, setData] = useState<PlanResponse | null>(null);
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [downgradeTo, setDowngradeTo] = useState<PlanTierOption | null>(null);
  const [showCancel, setShowCancel] = useState(false);
  const [busy, setBusy] = useState<null | "downgrade" | "cancel">(null);

  const isOwner = me?.user.id === tenant.owner_user_id;

  async function loadPlan() {
    try {
      const res = await api<PlanResponse>(`/tenants/${tenant.id}/plan`);
      setData(res);
      setError(null);
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  async function loadInvoices() {
    try {
      const res = await api<{ invoices: Invoice[] }>(`/tenants/${tenant.id}/plan/invoices`);
      setInvoices(res.invoices);
    } catch {
      setInvoices([]);
    }
  }

  useEffect(() => {
    setData(null);
    setInvoices(null);
    loadPlan();
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id]);

  async function refreshAll() {
    await Promise.all([loadPlan(), loadInvoices(), refetchMe()]);
  }

  async function confirmDowngrade() {
    if (!downgradeTo) return;
    setBusy("downgrade");
    setError(null);
    try {
      await mutate(`/tenants/${tenant.id}/plan/downgrade`, { plan: downgradeTo.plan_id });
      setDowngradeTo(null);
      await refreshAll();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function confirmCancel() {
    setBusy("cancel");
    setError(null);
    try {
      await mutate(`/tenants/${tenant.id}/plan/cancel`, {});
      setShowCancel(false);
      await refreshAll();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  if (error && !data) return <p className="text-sm text-red-600">{error}</p>;
  if (!data) return <p className="text-slate-400">Lädt…</p>;

  const { plan, tiers } = data;
  const usagePct =
    plan.cap == null
      ? 0
      : Math.min(100, Math.round((plan.member_count / Math.max(1, plan.cap)) * 100));
  const overCap = plan.cap != null && plan.member_count > plan.cap;
  const isPaid = plan.plan_id !== "frei";

  // Smaller paid tiers the club could schedule a downgrade to.
  const smallerTiers = tiers.filter(
    (t) => !t.contact_only && t.plan_id !== "frei" && tierRank(t.plan_id) < tierRank(plan.plan_id),
  );

  return (
    <div className="space-y-8">
      <Section title="Aktuelles Abo">
        <div className="rounded-xl border border-slate-200 p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Plan
              </div>
              <div className="mt-1 text-2xl font-semibold">{planLabel(plan.plan_id)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Mitglieder
              </div>
              <div className="mt-1 text-lg font-semibold">
                {plan.member_count} / {capLabel(plan.cap)}
              </div>
            </div>
          </div>
          {plan.cap != null && (
            <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className={"h-full " + (usagePct >= 90 ? "bg-red-500" : "bg-slate-900")}
                style={{ width: `${usagePct}%` }}
              />
            </div>
          )}
          {overCap && (
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Du hast mehr Mitglieder als dein Plan erlaubt. Archiviere Mitglieder oder upgrade, um
              wieder neue anzulegen.
            </p>
          )}
        </div>

        {isPaid && (
          <div className="rounded-xl border border-slate-200 p-5 text-sm">
            {plan.auto_renew ? (
              <p className="text-slate-600">
                Verlängert sich automatisch
                {plan.renews_at ? <> am {formatDate(plan.renews_at)}</> : null}.
              </p>
            ) : (
              <p className="text-amber-800">
                Gekündigt — läuft
                {plan.renews_at ? (
                  <> am {formatDate(plan.renews_at)}</>
                ) : (
                  <> zum Periodenende</>
                )}{" "}
                aus und wechselt danach auf <strong>Frei</strong>.
              </p>
            )}
            {plan.scheduled_plan_id && (
              <p className="mt-2 text-amber-800">
                Geplanter Wechsel auf <strong>{planLabel(plan.scheduled_plan_id)}</strong>
                {plan.renews_at ? <> am {formatDate(plan.renews_at)}</> : null}.
              </p>
            )}
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        {isOwner ? (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setShowUpgrade(true)}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              {isPaid ? "Plan upgraden" : "Auf bezahlten Plan upgraden"}
            </button>
            {isPaid && smallerTiers.length > 0 && (
              <DowngradeMenu tiers={smallerTiers} onPick={setDowngradeTo} />
            )}
            {isPaid && plan.auto_renew && (
              <button
                type="button"
                onClick={() => setShowCancel(true)}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Kündigen
              </button>
            )}
          </div>
        ) : (
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-500">
            Nur die Besitzerin oder der Besitzer des Vereins kann das Abo verwalten.
          </p>
        )}
      </Section>

      <Section title="Rechnungen von Vereinfach">
        {invoices == null ? (
          <p className="text-sm text-slate-400">Lädt…</p>
        ) : invoices.length === 0 ? (
          <p className="text-sm text-slate-500">Noch keine Rechnungen.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full min-w-[480px] text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium">Nummer</th>
                  <th className="px-4 py-2.5 text-left font-medium">Datum</th>
                  <th className="px-4 py-2.5 text-right font-medium">Betrag</th>
                  <th className="px-4 py-2.5 text-left font-medium">Status</th>
                  <th className="px-4 py-2.5 text-right font-medium">PDF</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-slate-100">
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      {inv.number ? (
                        <span className="font-mono text-xs">{inv.number}</span>
                      ) : (
                        <span className="text-slate-500 italic">Entwurf</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-slate-500">
                      {formatIsoDate(inv.invoice_date)}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {formatCHF(inv.total_rappen)}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge status={inv.display_status} />
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <a
                        href={apiUrl(
                          `/tenants/${tenant.id}/plan/invoices/${inv.id}/pdf?variant=auto`,
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-600 underline hover:text-slate-900"
                      >
                        PDF
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {showUpgrade && (
        <UpgradeModal
          tenantId={tenant.id}
          tiers={tiers}
          recommended={data.recommended_plan}
          termsVersion={data.terms_version}
          memberCount={plan.member_count}
          onClose={() => setShowUpgrade(false)}
          onUpgraded={async () => {
            setShowUpgrade(false);
            await refreshAll();
          }}
        />
      )}

      {downgradeTo && (
        <Modal title="Plan ändern" onClose={() => setDowngradeTo(null)}>
          <p className="text-sm text-slate-600">
            Wechsel auf <strong>{planLabel(downgradeTo.plan_id)}</strong> (bis{" "}
            {capLabel(downgradeTo.cap)} Mitglieder) wird auf das Ende der aktuellen Periode geplant.
            Bis dahin bleibt dein aktuelles Abo aktiv.
          </p>
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setDowngradeTo(null)}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
            >
              Abbrechen
            </button>
            <button
              type="button"
              disabled={busy === "downgrade"}
              onClick={confirmDowngrade}
              className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {busy === "downgrade" ? "Einen Moment…" : "Wechsel planen"}
            </button>
          </div>
        </Modal>
      )}

      {showCancel && (
        <Modal title="Abo kündigen" onClose={() => setShowCancel(false)}>
          <p className="text-sm text-slate-600">
            Dein Abo läuft auf das Ende der aktuellen Periode
            {plan.renews_at ? (
              <>
                {" "}
                (<strong>{formatDate(plan.renews_at)}</strong>)
              </>
            ) : null}{" "}
            aus. Bis dahin bleibt alles unverändert nutzbar.
          </p>
          <p className="mt-3 text-sm text-slate-600">
            Danach wirst du automatisch auf <strong>Frei</strong> zurückgestuft. Deine Daten bleiben
            erhalten.
          </p>
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowCancel(false)}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
            >
              Zurück
            </button>
            <button
              type="button"
              disabled={busy === "cancel"}
              onClick={confirmCancel}
              className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {busy === "cancel" ? "Kündige…" : "Kündigung bestätigen"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function DowngradeMenu({
  tiers,
  onPick,
}: {
  tiers: PlanTierOption[];
  onPick: (t: PlanTierOption) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
      >
        Plan ändern
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 z-20 mt-2 min-w-56 rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
            {tiers.map((t) => (
              <button
                key={t.plan_id}
                type="button"
                onClick={() => {
                  setOpen(false);
                  onPick(t);
                }}
                className="block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                {planLabel(t.plan_id)} · bis {capLabel(t.cap)} Mitglieder
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function UpgradeModal({
  tenantId,
  tiers,
  recommended,
  termsVersion,
  memberCount,
  onClose,
  onUpgraded,
}: {
  tenantId: string;
  tiers: PlanTierOption[];
  recommended: PlanId;
  termsVersion: string;
  memberCount: number;
  onClose: () => void;
  onUpgraded: () => void;
}) {
  const { settings } = useSettings();
  const selectable = tiers.filter((t) => !t.contact_only && t.plan_id !== "frei");
  const xl = tiers.find((t) => t.contact_only) ?? null;

  const initialPlan =
    selectable.find((t) => t.plan_id === recommended && !t.disabled)?.plan_id ??
    selectable.find((t) => !t.disabled)?.plan_id ??
    selectable[0]?.plan_id ??
    "klein";

  const [plan, setPlan] = useState<PlanId>(initialPlan);
  const [form, setForm] = useState(() => prefillFromSettings(settings));
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Settings may load after the modal opens; backfill empty fields once.
  useEffect(() => {
    if (settings) setForm((f) => mergePrefill(f, settings));
  }, [settings]);

  function set<K extends keyof UpgradeForm>(k: K, v: UpgradeForm[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  const zipValid = /^[1-9]\d{3}$/.test(form.zip.trim());
  const canSubmit =
    consent &&
    !!plan &&
    form.legal_name.trim() &&
    form.address_line1.trim() &&
    zipValid &&
    form.city.trim() &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.billing_email.trim()) &&
    form.contact_person.trim();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await mutate(`/tenants/${tenantId}/plan/upgrade`, {
        plan,
        legal_name: form.legal_name.trim(),
        address_line1: form.address_line1.trim(),
        address_line2: form.address_line2.trim() || null,
        zip: form.zip.trim(),
        city: form.city.trim(),
        billing_email: form.billing_email.trim(),
        contact_person: form.contact_person.trim(),
        uid: form.uid.trim() || null,
        consent: true,
        terms_version: termsVersion,
      });
      onUpgraded();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Modal title="Plan upgraden" onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <span className="text-xs font-medium text-slate-500">Plan wählen</span>
          <div className="mt-2 space-y-2">
            {selectable.map((t) => (
              <label
                key={t.plan_id}
                className={
                  "flex items-start gap-2 rounded-lg border p-3 text-sm " +
                  (t.disabled
                    ? "border-slate-100 bg-slate-50 opacity-60"
                    : plan === t.plan_id
                      ? "border-slate-900"
                      : "border-slate-200")
                }
              >
                <input
                  type="radio"
                  name="plan"
                  className="mt-0.5"
                  disabled={t.disabled}
                  checked={plan === t.plan_id}
                  onChange={() => setPlan(t.plan_id)}
                />
                <span className="flex-1">
                  <span className="font-medium text-slate-900">{planLabel(t.plan_id)}</span>
                  <span className="ml-2 text-slate-500">bis {capLabel(t.cap)} Mitglieder</span>
                  {t.disabled && (
                    <span className="block text-xs text-slate-500">
                      Zu klein für {memberCount} Mitglieder.
                    </span>
                  )}
                </span>
                <span className="whitespace-nowrap font-medium">
                  {formatCHF(t.price_rappen)} <span className="text-xs text-slate-500">/ Jahr</span>
                </span>
              </label>
            ))}
            {xl && (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 p-3 text-sm">
                <span>
                  <span className="font-medium text-slate-900">{planLabel(xl.plan_id)}</span>
                  <span className="ml-2 text-slate-500">Für grosse Vereine</span>
                </span>
                <a
                  href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("XL-Plan Anfrage")}`}
                  className="whitespace-nowrap font-medium text-slate-900 underline hover:no-underline"
                >
                  Auf Anfrage
                </a>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-slate-100 pt-4">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Rechnungsadresse
          </span>
          <div className="mt-2 space-y-3">
            <Field label="Juristischer Name">
              <input
                required
                value={form.legal_name}
                onChange={(e) => set("legal_name", e.target.value)}
                className="input"
              />
            </Field>
            <Field label="Strasse">
              <input
                required
                value={form.address_line1}
                onChange={(e) => set("address_line1", e.target.value)}
                className="input"
              />
            </Field>
            <Field label="Adresszusatz (optional)">
              <input
                value={form.address_line2}
                onChange={(e) => set("address_line2", e.target.value)}
                className="input"
              />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="PLZ">
                <input
                  required
                  value={form.zip}
                  onChange={(e) => set("zip", e.target.value)}
                  className={"input " + (form.zip && !zipValid ? "border-red-300" : "")}
                />
              </Field>
              <div className="col-span-2">
                <Field label="Ort">
                  <input
                    required
                    value={form.city}
                    onChange={(e) => set("city", e.target.value)}
                    className="input"
                  />
                </Field>
              </div>
            </div>
            <Field label="Land">
              <input
                value="Schweiz"
                disabled
                readOnly
                className="input bg-slate-50 text-slate-500"
              />
            </Field>
            <Field label="Rechnungs-E-Mail">
              <input
                required
                type="email"
                value={form.billing_email}
                onChange={(e) => set("billing_email", e.target.value)}
                className="input"
              />
            </Field>
            <Field label="Kontaktperson">
              <input
                required
                value={form.contact_person}
                onChange={(e) => set("contact_person", e.target.value)}
                className="input"
              />
            </Field>
            <Field label="UID (optional)">
              <input
                value={form.uid}
                onChange={(e) => set("uid", e.target.value)}
                placeholder="CHE-123.456.789"
                className="input"
              />
            </Field>
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          <span className="text-slate-600">
            Ich bestelle den Plan kostenpflichtig und akzeptiere die Allgemeinen
            Geschäftsbedingungen.
          </span>
        </label>

        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={busy || !canSubmit}
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {busy ? "Einen Moment…" : "Kostenpflichtig bestellen"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

type UpgradeForm = {
  legal_name: string;
  address_line1: string;
  address_line2: string;
  zip: string;
  city: string;
  billing_email: string;
  contact_person: string;
  uid: string;
};

function prefillFromSettings(settings: Settings | null): UpgradeForm {
  return {
    legal_name: settings?.club_name ?? "",
    address_line1: settings?.address_line1 ?? "",
    address_line2: settings?.address_line2 ?? "",
    zip: settings?.zip ?? "",
    city: settings?.city ?? "",
    billing_email: "",
    contact_person: "",
    uid: settings?.vat_number ?? "",
  };
}

/** Fill only the still-empty fields once settings arrive after mount. */
function mergePrefill(form: UpgradeForm, settings: Settings): UpgradeForm {
  return {
    ...form,
    legal_name: form.legal_name || settings.club_name || "",
    address_line1: form.address_line1 || settings.address_line1 || "",
    address_line2: form.address_line2 || settings.address_line2 || "",
    zip: form.zip || settings.zip || "",
    city: form.city || settings.city || "",
    uid: form.uid || settings.vat_number || "",
  };
}
